import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { securityApi } from "@/services/security.service";
import { removeToken } from "@/lib/api-client-v2";
import { Lock } from "lucide-react";

/** Статический фон заставки блокировки (из public/, не бандлится Vite). */
const SCREEN_LOCK_BG_VIDEO = "/media/screen-lock/earth.mp4";

type Props = {
  onUnlocked: (unlockToken: string) => void;
};

function extractRetryAfter(err: unknown): number | null {
  const msg = err instanceof Error ? err.message : String(err);
  const m = msg.match(/retry_after["']?\s*[:=]\s*(\d+)/i);
  if (m) return Number(m[1]);
  if (msg.includes("423") || msg.includes("429") || /lockout/i.test(msg)) return 60;
  return null;
}

export function ScreenLockOverlay({ onUnlocked }: Props) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lockSeconds, setLockSeconds] = useState(0);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [preferReducedMotion, setPreferReducedMotion] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setPreferReducedMotion(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || preferReducedMotion) return;
    void el.play().catch(() => {
      /* autoplay может быть запрещён — остаётся постер/тёмный фон */
    });
  }, [preferReducedMotion]);

  useEffect(() => {
    if (lockSeconds <= 0) return;
    const t = setInterval(() => setLockSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [lockSeconds]);

  const submit = async (value: string) => {
    if (busy || lockSeconds > 0) return;
    if (value.length < 4) return;
    setBusy(true);
    setError(null);
    try {
      const res = await securityApi.verifyPin(value);
      onUnlocked(res.unlock_token);
      setPin("");
    } catch (e) {
      const retry = extractRetryAfter(e);
      if (retry) setLockSeconds(retry);
      setError("Неверный PIN");
      setPin("");
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const onChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    setPin(digits);
    if (digits.length >= 6) void submit(digits);
  };

  const onForgot = async () => {
    try {
      await securityApi.forgotPin();
    } catch {
      /* всё равно выходим */
    }
    removeToken();
    window.location.href = "/login";
  };

  const dots = Array.from({ length: Math.max(pin.length, 6) }, (_, i) => i < pin.length);

  return (
    <div
      data-testid="screen-lock-overlay"
      className="fixed inset-0 z-[300] flex items-center justify-center overflow-hidden bg-slate-950"
      role="dialog"
      aria-modal="true"
      aria-label="Экран заблокирован"
    >
      {!preferReducedMotion ? (
        <video
          ref={videoRef}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          src={SCREEN_LOCK_BG_VIDEO}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden
        />
      ) : null}
      <div className="absolute inset-0 bg-slate-950/55 backdrop-blur-[2px]" aria-hidden />

      <div className="relative z-10 mx-4 w-full max-w-sm space-y-6 rounded-lg border border-white/15 bg-background/90 p-6 shadow-lg backdrop-blur-md">
        <div className="flex flex-col items-center gap-2 text-center">
          <Lock className="h-8 w-8 text-muted-foreground" aria-hidden />
          <h2 className="text-lg font-semibold">Экран заблокирован</h2>
          <p className="text-sm text-muted-foreground">Введите PIN для продолжения работы</p>
        </div>

        <div className="flex justify-center gap-2" aria-hidden>
          {dots.map((filled, i) => (
            <span
              key={i}
              className={`h-3 w-3 rounded-full border ${filled ? "bg-foreground" : "bg-transparent"}`}
            />
          ))}
        </div>

        <Input
          ref={inputRef}
          type="password"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          value={pin}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit(pin);
          }}
          disabled={busy || lockSeconds > 0}
          className="text-center tracking-widest"
          aria-label="PIN-код"
        />

        {error ? <p className="text-center text-sm text-destructive">{error}</p> : null}
        {lockSeconds > 0 ? (
          <p className="text-center text-sm text-muted-foreground">
            Повторите через {lockSeconds} с
          </p>
        ) : null}

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            onClick={() => void submit(pin)}
            disabled={busy || lockSeconds > 0 || pin.length < 4}
          >
            Разблокировать
          </Button>
          <Button type="button" variant="ghost" onClick={() => setForgotOpen(true)}>
            Забыл PIN
          </Button>
        </div>
      </div>

      <AlertDialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <AlertDialogContent className="z-[310]">
          <AlertDialogHeader>
            <AlertDialogTitle>Сброс PIN</AlertDialogTitle>
            <AlertDialogDescription>
              Сессия будет завершена. После повторного входа (с паролем и 2FA при наличии) вы
              сможете установить новый PIN. Сброс без аутентификации недоступен.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={() => void onForgot()}>Выйти и продолжить</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
